import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { Menu, X, Home, Dices, Settings, LogOut, Lightbulb, PieChart, Calendar, Shield, ArrowLeft, Building2, HelpCircle } from 'lucide-react';
import { fetchClubBetaFeatures, isBetaEnabled } from '../utils/betaFeatures';

const Navigation = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showClubInfo, setShowClubInfo] = useState(false);
  const [gameNightsEnabled, setGameNightsEnabled] = useState(false);
  const infoRef = useRef(null);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);
  
  // Check if SuperAdmin is impersonating a club
  const isImpersonating = user?.isImpersonating;
  const clubName = user?.club?.name || 'Klubi';
  const clubDescription = user?.club?.description?.trim() || '';

  useEffect(() => {
    let cancelled = false;
    fetchClubBetaFeatures().then((features) => {
      if (!cancelled) setGameNightsEnabled(isBetaEnabled(features, 'game_nights'));
    });
    return () => { cancelled = true; };
  }, [user?.club?.id]);

  useEffect(() => {
    if (!showClubInfo) return;
    const onPointerDown = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) {
        setShowClubInfo(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [showClubInfo]);

  // Handle returning to SuperAdmin from impersonation
  const handleBackToSuperAdmin = () => {
    // Restore original SuperAdmin session
    const originalData = localStorage.getItem('masaklubi_superadmin_original');
    
    if (originalData) {
      try {
        const { user, token, clubKey } = JSON.parse(originalData);
        
        // Restore original session
        localStorage.setItem('masaklubi_user', JSON.stringify(user));
        if (token) localStorage.setItem('masaklubi_access_token', token);
        if (clubKey) localStorage.setItem('masaklubi_club_key', clubKey);
        
        // Clean up impersonation data
        localStorage.removeItem('masaklubi_superadmin_original');
        
        console.log('[Navigation] Restored SuperAdmin session');
        
        // Redirect to SuperAdmin dashboard
        window.location.href = '/superadmin';
        return;
      } catch (err) {
        console.error('[Navigation] Failed to restore SuperAdmin session:', err);
      }
    }
    
    // Fallback: clear everything and go to root
    localStorage.removeItem('masaklubi_user');
    localStorage.removeItem('masaklubi_access_token');
    localStorage.removeItem('masaklubi_club_key');
    localStorage.removeItem('masaklubi_superadmin_verified');
    localStorage.removeItem('masaklubi_superadmin_original');
    window.location.href = '/';
  };

  return (
    <nav className="navbar">
      {/* Impersonation banner */}
      {isImpersonating && (
        <div className="impersonation-banner">
          <Building2 size={16} />
          <span>Katselet klubia: <strong>{clubName}</strong></span>
          <button onClick={handleBackToSuperAdmin} className="back-to-superadmin">
            <ArrowLeft size={14} /> Takaisin SuperAdminiin
          </button>
        </div>
      )}
      
      <div className="nav-brand">
        <Dices /> {clubName}
      </div>
      
      <button className="mobile-menu-btn" onClick={toggleMenu}>
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      <div className={`nav-links ${isOpen ? 'open' : ''}`}>
        <NavLink to="/" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
          <Home size={18} /> Etusivu
        </NavLink>
        <NavLink to="/games" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
          <Dices size={18} /> Pelit
        </NavLink>
        {gameNightsEnabled && (
          <NavLink to="/events" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
            <Calendar size={18} /> Peli-illat
          </NavLink>
        )}
        <NavLink to="/statistics" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
          <PieChart size={18} /> Analyysit
        </NavLink>
        <NavLink to="/suggestions" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
          <Lightbulb size={18} /> Kehityslaatikko
        </NavLink>
        {(user.role === 'admin' || user.role === 'board' || isImpersonating) && (
          <NavLink to="/admin" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
            <Settings size={18} /> Admin
          </NavLink>
        )}
        {user.role === 'superadmin' && !isImpersonating && (
          <>
            <NavLink to="/superadmin" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
              <Shield size={18} /> Super Admin
            </NavLink>
            <NavLink to="/admin" className={({isActive}) => `nav-link ${isActive ? 'active' : ''}`} onClick={closeMenu}>
              <Settings size={18} /> Admin
            </NavLink>
          </>
        )}
        {clubDescription && (
          <div className="nav-club-info" ref={infoRef}>
            <button
              type="button"
              className="nav-link nav-club-info-btn"
              aria-label="Klubin kuvaus"
              aria-expanded={showClubInfo}
              title="Klubin kuvaus"
              onClick={() => setShowClubInfo((v) => !v)}
            >
              <HelpCircle size={18} />
            </button>
            {showClubInfo && (
              <div className="nav-club-info-popover" role="dialog" aria-label="Klubin kuvaus">
                <p>{clubDescription}</p>
              </div>
            )}
          </div>
        )}
        <button onClick={onLogout} className="nav-link" style={{background: 'none', border: 'none', cursor: 'pointer'}}>
          <LogOut size={18} />
        </button>
      </div>
    </nav>
  );
};

export default Navigation;
